import { StationAvatar } from '@app/components/base/stations/StationAvatar';
import { LDateTime } from '@app/components/date/LDateTime';
import { useStation } from '@app/core/stations/hooks/useStation';
import { formatDuration } from '@mi-front/core';
import { Box, Typography } from '@mui/material';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { TimelineTooltipPayload } from '../../lib/timeline-tooltip-types';
import { EventTypeChip } from '../../shared/EventTypeChip';

const streamAvailabilityTimelineTooltipMessages = defineMessages({
  range: {
    id: 'logger.reports.streamQuality.availability.tooltip.range',
    defaultMessage: '{from} – {to}',
  },
  duration: {
    id: 'logger.reports.streamQuality.availability.tooltip.duration',
    defaultMessage: 'Duración: {duration}',
  },
  clickOpenLogger: {
    id: 'logger.reports.streamQuality.availability.tooltip.clickOpenLogger',
    defaultMessage: 'Click para mostrar en Logger',
  },
});

function getDurationMs(payload: TimelineTooltipPayload): number {
  if (payload.type === 'issue') {
    return Math.max(0, Math.round(payload.issue.durationSeconds * 1000));
  }

  return Math.max(0, payload.end.getTime() - payload.start.getTime());
}

export type TimelineTooltipContentProps = {
  payload: TimelineTooltipPayload;
};

/**
 * Tooltip body for the stream availability timeline: station (avatar + name), type chip, time range
 * (via `LDateTime` with the station `tz`). If the station zone differs from the browser, a short
 * timezone label is shown to the right of the range; then duration and Logger hint.
 */
export function TimelineTooltipContent({ payload }: TimelineTooltipContentProps) {
  const { $t } = useIntl();
  const station = useStation(payload.stationId);
  const durationMs = getDurationMs(payload);
  const durationText = formatDuration(durationMs);
  const stationTz = station.timezone;

  return (
    <Box sx={{ p: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 1,
          minWidth: 0,
        }}
      >
        <StationAvatar
          station={station}
          stationId={payload.stationId}
          size={16}
          sx={{ flexShrink: 0 }}
        />
        <Typography
          component="span"
          sx={{ typography: 'caption', color: 'text.secondary', minWidth: 0 }}
        >
          {station.name}
        </Typography>
      </Box>
      <EventTypeChip payload={payload} />
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
        }}
      >
        <Typography
          component="div"
          sx={{
            typography: 'caption',
            color: 'text.secondary',
          }}
        >
          <FormattedMessage
            {...streamAvailabilityTimelineTooltipMessages.range}
            values={{
              from: <LDateTime value={payload.start.toISOString()} tz={stationTz} />,
              to: <LDateTime value={payload.end.toISOString()} tz={stationTz} showTz />,
            }}
          />
        </Typography>
      </Box>
      <Typography sx={{ typography: 'caption', color: 'text.secondary' }}>
        {$t(streamAvailabilityTimelineTooltipMessages.duration, { duration: durationText })}
      </Typography>
      <Typography
        sx={{
          typography: 'caption',
          color: 'text.disabled',
          mt: 0.5,
        }}
      >
        {$t(streamAvailabilityTimelineTooltipMessages.clickOpenLogger)}
      </Typography>
    </Box>
  );
}
